import { BasicButton, GhostButton, Stack } from "@certn/cascadia";
import { FormLabelRAC } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Props } from "./types";
import {
  copyValuesWithoutKeyAtPath,
  getFieldName,
  justReturnControl,
  schemaIsOptional,
} from "./utils";

/** This wrapper adds an interface for optional fields (where the key is optional in the
 * data) This is only present if the form `isCheckResults` and is not `readonly` or
 * `hidden`.
 *
 * If the schema does not have the field key as required then the following elements
 * will be rendered:
 * - If the value is undefined: an "Add" button next to the label
 * - If the value is not undefined: the control to be rendered and a "Remove" button
 * which sets deletes the key from the form values
 */
export function CheckResultsOptionalWrapper(props: Props) {
  const {
    children,
    schema,
    isCheckResults,
    readonly,
    hidden,
    data,
    label,
    path,
    values,
    setValues,
  } = props;
  const { t } = useTranslation();
  const [showField, setShowField] = useState(data !== undefined);

  const fieldName = useMemo(() => getFieldName(path), [path]);

  const isOptional = useMemo(
    () => schemaIsOptional(schema, fieldName),
    [schema, fieldName]
  );

  const handleAddValue = () => {
    setShowField(true);
  };

  const handleClearValue = () => {
    const newValue = copyValuesWithoutKeyAtPath(values, path);
    if (newValue) {
      setValues(newValue);
      setShowField(false);
    }
  };

  if (justReturnControl(readonly, hidden, isCheckResults, isOptional)) {
    return <>{children}</>;
  }

  if (!showField && data === undefined) {
    return (
      <Stack orientation="horizontal">
        {label && <FormLabelRAC>{label}</FormLabelRAC>}
        <GhostButton onPress={handleAddValue}>{t("Add")}</GhostButton>
      </Stack>
    );
  }

  return (
    <Stack>
      <div className={styles.fieldWrapper}>{children}</div>
      <BasicButton onPress={handleClearValue}>
        {t("Remove {{label}}", { label: fieldName })}
      </BasicButton>
    </Stack>
  );
}

const styles = {
  fieldWrapper: css({ flexGrow: 1 }),
};
